<script>
  // A date picker that looks the same everywhere: a button that opens a month calendar. Value is "YYYY-MM-DD" (or '').
  import { inputCls } from '../../lib/ui.js';
  import CalendarDays from 'lucide-svelte/icons/calendar-days';
  import ChevronLeft from 'lucide-svelte/icons/chevron-left';
  import ChevronRight from 'lucide-svelte/icons/chevron-right';

  let { value = $bindable(''), min = '', id = '', disabled = false, placeholder = 'Pick a date', 'aria-label': ariaLabel = undefined } = $props();

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  const pad = (n) => String(n).padStart(2, '0');
  const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
  const parse = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s || '') ? { y: +s.slice(0, 4), m: +s.slice(5, 7) - 1, d: +s.slice(8, 10) } : null);
  const todayIso = () => { const t = new Date(); return iso(t.getFullYear(), t.getMonth(), t.getDate()); };

  let open = $state(false);
  let root;
  let view = $state({ y: 0, m: 0 });
  function show() {
    if (disabled) return;
    const p = parse(value) || parse(todayIso());
    view = { y: p.y, m: p.m };
    open = true;
  }
  const label = $derived.by(() => {
    const p = parse(value);
    if (!p) return '';
    return new Date(p.y, p.m, p.d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  });
  // The weeks of the shown month, Monday first; null pads the first week.
  const cells = $derived.by(() => {
    const first = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
    const count = new Date(view.y, view.m + 1, 0).getDate();
    return [...Array(first).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  });
  function step(n) { const d = new Date(view.y, view.m + n, 1); view = { y: d.getFullYear(), m: d.getMonth() }; }
  function pick(day) { const v = iso(view.y, view.m, day); if (min && v < min) return; value = v; open = false; }
  const today = todayIso();
</script>

<svelte:window onpointerdown={(e) => { if (open && root && !root.contains(e.target)) open = false; }} onkeydown={(e) => { if (open && e.key === 'Escape') { e.stopPropagation(); open = false; } }} />

<div class="relative" bind:this={root}>
  <button type="button" {id} {disabled} aria-haspopup="dialog" aria-expanded={open} aria-label={ariaLabel} onclick={() => (open ? (open = false) : show())}
    class="{inputCls} items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50">
    <span class={label ? '' : 'text-muted-foreground'}>{label || placeholder}</span>
    <CalendarDays size={15} class="shrink-0 text-muted-foreground" />
  </button>
  {#if open}
    <div role="dialog" aria-label="Choose a date" class="absolute left-0 z-[60] mt-1 w-72 rounded-lg border border-border bg-background p-3 shadow-lg">
      <div class="mb-2 flex items-center justify-between">
        <button type="button" class="rounded-md p-1.5 hover:bg-accent" aria-label="Previous month" onclick={() => step(-1)}><ChevronLeft size={16} /></button>
        <p class="text-sm font-medium" aria-live="polite">{MONTHS[view.m]} {view.y}</p>
        <button type="button" class="rounded-md p-1.5 hover:bg-accent" aria-label="Next month" onclick={() => step(1)}><ChevronRight size={16} /></button>
      </div>
      <div class="grid grid-cols-7 gap-0.5 text-center">
        {#each DAYS as d}<span class="py-1 text-xs text-muted-foreground">{d}</span>{/each}
        {#each cells as day}
          {#if day === null}<span></span>
          {:else}
            {@const v = iso(view.y, view.m, day)}
            {@const off = Boolean(min) && v < min}
            <button type="button" disabled={off} aria-pressed={v === value} aria-label={new Date(view.y, view.m, day).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} onclick={() => pick(day)}
              class="h-9 rounded-md text-sm tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-30 {v === value ? 'bg-foreground text-background' : v === today ? 'border border-border font-semibold hover:bg-accent' : 'hover:bg-accent'}">{day}</button>
          {/if}
        {/each}
      </div>
      <div class="mt-2 flex justify-between border-t border-border pt-2">
        <button type="button" class="rounded-md px-2 py-1 text-xs font-medium hover:bg-accent disabled:opacity-40" disabled={Boolean(min) && today < min} onclick={() => { value = today; open = false; }}>Today</button>
        <button type="button" class="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent" onclick={() => (open = false)}>Close</button>
      </div>
    </div>
  {/if}
</div>
